// Same-bucket aggregated calendar chip. Replaces N CalendarEventChips
// when N ≥ 2 activities share kind + contract + severity on the same
// day (design doc §3). Clicking opens the existing DayPopover scoped
// to the bucket; this component just bubbles a parameter-free onClick.
//
// Visual contract: same surface vocabulary as CalendarEventChip (a
// red bucket reads red; a brand bucket reads brand; neutral reads
// against a light theme). Leading contract ref + type dot match the
// individual chip's leading element order so the calendar reads
// uniformly whether or not a cell is aggregated.

import {
  KIND_DOT_BG,
  KIND_LABEL,
  KIND_LABEL_PLURAL,
  type ScheduleKind,
} from "./kindVocabulary";
import type { ActivitySeverity } from "../../../types/schedule";

interface AggregatedCalendarEventChipProps {
  count: number;
  kind: ScheduleKind;
  status: ActivitySeverity;
  contractRef: string;
  onClick: () => void;
}

export default function AggregatedCalendarEventChip({
  count,
  kind,
  status,
  contractRef,
  onClick,
}: AggregatedCalendarEventChipProps) {
  // Mirrors CalendarEventChip surface logic — slate body text on every
  // severity so the chip's text colour stays consistent across a row;
  // amber slippage gets its own visible surface treatment.
  let surface: string;
  if (status === "red") {
    surface =
      "bg-red-500/10 text-slate-800 border-red-500 dark:bg-red-500/15 dark:text-slate-100";
  } else if (status === "brand") {
    surface =
      "bg-brand-500/12 text-slate-800 border-brand-500 dark:bg-brand-500/15 dark:text-slate-100";
  } else if (status === "amber") {
    surface =
      "bg-amber-500/10 text-slate-800 border-amber-500 dark:bg-amber-500/15 dark:text-slate-100";
  } else {
    surface =
      "bg-white text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600";
  }

  const label = count === 1 ? KIND_LABEL[kind] : KIND_LABEL_PLURAL[kind];

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${count} ${label} on this day`}
      className={`flex w-full items-center gap-1.5 overflow-hidden rounded-sm border-l-[3px] py-[3px] pl-1 pr-1.5 text-left text-[11px] font-medium leading-tight transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 ${surface}`}
    >
      <span className="shrink-0 font-mono text-[9.5px] text-slate-500 dark:text-slate-400">
        {contractRef}
      </span>
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${KIND_DOT_BG[kind]}`}
      />
      <span className="truncate">
        {count} × {label}
      </span>
    </button>
  );
}
